import { useCallback, useState, useRef, useEffect } from 'react';
import { cn } from '@/utils';
import { useModelLoader } from '@/hooks';
import type { Model3D } from '@/types';
import { ModelScene, defaultLightingSettings, type LightingSettings } from './ModelScene';
import styles from './ModelViewer.module.css';

function useInView(options?: IntersectionObserverInit) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        // Once visible, stop observing (load only once)
        observer.disconnect();
      }
    }, options);

    observer.observe(element);

    return () => observer.disconnect();
  }, [options]);

  return { ref, isInView };
}

const ENVIRONMENT_PRESETS = [
  { value: 'studio', label: 'Studio' },
  { value: 'city', label: 'Ciudad' },
  { value: 'sunset', label: 'Atardecer' },
  { value: 'dawn', label: 'Amanecer' },
  { value: 'night', label: 'Noche' },
  { value: 'warehouse', label: 'Almacén' },
  { value: 'forest', label: 'Bosque' },
  { value: 'park', label: 'Parque' },
  { value: 'lobby', label: 'Lobby' },
  { value: 'apartment', label: 'Apartamento' },
] as const;

interface ModelViewerProps {
  models: Model3D[];
  variant?: 'compact' | 'full';
  autoRotate?: boolean;
  showControls?: boolean;
  showInfo?: boolean;
  showToolbar?: boolean;
  lazy?: boolean;
  className?: string;
}

const INTERSECTION_OPTIONS: IntersectionObserverInit = {
  rootMargin: '100px', // Start loading slightly before it's visible
};

export function ModelViewer({
  models,
  variant = 'compact',
  autoRotate = true,
  showControls = true,
  showInfo = true,
  showToolbar = true,
  lazy = false,
  className,
}: ModelViewerProps) {
  const { ref: containerRef, isInView } = useInView(INTERSECTION_OPTIONS);
  const shouldLoad = !lazy || isInView;

  const {
    currentModel,
    currentIndex,
    totalModels,
    isLoading,
    goToNext,
    goToPrevious,
    setLoading,
  } = useModelLoader({ models, randomStart: true });

  const [lightingSettings, setLightingSettings] = useState<LightingSettings>(defaultLightingSettings);
  const [isToolbarOpen, setIsToolbarOpen] = useState(false);
  const [isPanMode, setIsPanMode] = useState(false);

  const handleLoaded = useCallback(() => {
    setLoading(false);
  }, [setLoading]);

  const updateSetting = <K extends keyof LightingSettings>(key: K, value: LightingSettings[K]) => {
    setLightingSettings((prev) => ({ ...prev, [key]: value }));
  };

  if (!models.length) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        styles.viewer,
        variant === 'compact' ? styles.viewerCompact : styles.viewerFull,
        className
      )}
    >
      {shouldLoad ? (
        <div className={styles.canvas}>
          <ModelScene
            key={currentModel.id}
            model={currentModel}
            autoRotate={autoRotate}
            onLoaded={handleLoaded}
            lightingSettings={lightingSettings}
            touchPanMode={isPanMode}
          />
        </div>
      ) : (
        <div className={styles.lazyPlaceholder}>
          <span className={styles.lazyPlaceholderText}>Visor 3D</span>
        </div>
      )}

      {shouldLoad && isLoading && (
        <div className={styles.loadingOverlay}>
          <span className={styles.loadingText}>Cargando modelo...</span>
        </div>
      )}

      {shouldLoad && showControls && totalModels > 1 && (
        <>
          <button
            className={cn(styles.arrow, styles.arrowLeft)}
            onClick={goToPrevious}
            aria-label="Modelo anterior"
          >
            &#8249;
          </button>
          <button
            className={cn(styles.arrow, styles.arrowRight)}
            onClick={goToNext}
            aria-label="Siguiente modelo"
          >
            &#8250;
          </button>
        </>
      )}

      {shouldLoad && (
        <div className={styles.counter}>
          {currentIndex + 1} / {totalModels}
        </div>
      )}

      {shouldLoad && showInfo && (
        <div className={styles.modelInfo}>
          <h4 className={styles.modelName}>{currentModel.name}</h4>
          <p className={styles.modelDescription}>{currentModel.description}</p>
        </div>
      )}

      {/* Pan Mode Toggle (for touch devices) */}
      {shouldLoad && (
        <button
          className={cn(styles.panToggle, isPanMode && styles.panToggleActive)}
          onClick={() => setIsPanMode(!isPanMode)}
          aria-label={isPanMode ? 'Modo rotar' : 'Modo arrastrar'}
          title={isPanMode ? 'Cambiar a rotar' : 'Cambiar a arrastrar'}
        >
          {isPanMode ? (
            // Hand/Pan icon
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18 24h-6.55c-1.08 0-2.14-.45-2.89-1.23l-5.5-5.52c-.56-.56-.56-1.47 0-2.03.24-.24.54-.39.87-.44.33-.05.67.01.96.18l2.61 1.55V4c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v6h.5V2c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v8h.5V3c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v7h.5V6c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v12c0 3.31-2.69 6-6 6z"/>
            </svg>
          ) : (
            // Rotate icon
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
            </svg>
          )}
        </button>
      )}

      {/* Lighting Toolbar */}
      {shouldLoad && showToolbar && (
        <>
          <button
            className={cn(styles.toolbarToggle, isToolbarOpen && styles.toolbarToggleActive)}
            onClick={() => setIsToolbarOpen(!isToolbarOpen)}
            aria-label="Toggle lighting controls"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41.39.39 1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41.39.39 1.03.39 1.41 0l1.06-1.06z" />
            </svg>
          </button>

          {isToolbarOpen && (
            <div className={styles.toolbar}>
              <div className={styles.toolbarHeader}>
                <span>Iluminación</span>
                <button
                  className={styles.toolbarClose}
                  onClick={() => setIsToolbarOpen(false)}
                >
                  ×
                </button>
              </div>

              <div className={styles.toolbarContent}>
                {/* Environment Section */}
                <div className={styles.toolbarGroup}>
                  <div className={styles.toolbarGroupTitle}>Entorno HDR</div>

                  <div className={styles.toolbarSection}>
                    <label className={styles.toolbarLabel}>Preset</label>
                    <select
                      className={styles.toolbarSelect}
                      value={lightingSettings.environmentPreset}
                      onChange={(e) =>
                        updateSetting('environmentPreset', e.target.value as LightingSettings['environmentPreset'])
                      }
                    >
                      {ENVIRONMENT_PRESETS.map((preset) => (
                        <option key={preset.value} value={preset.value}>
                          {preset.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className={styles.toolbarSection}>
                    <label className={styles.toolbarLabel}>
                      Intensidad: {lightingSettings.environmentIntensity.toFixed(1)}
                    </label>
                    <input
                      type="range"
                      className={styles.toolbarSlider}
                      min="0"
                      max="3"
                      step="0.1"
                      value={lightingSettings.environmentIntensity}
                      onChange={(e) => updateSetting('environmentIntensity', parseFloat(e.target.value))}
                    />
                  </div>

                  <div className={styles.toolbarSection}>
                    <label className={styles.toolbarLabel}>
                      Rotación: {Math.round((lightingSettings.environmentRotation / Math.PI) * 180)}°
                    </label>
                    <input
                      type="range"
                      className={styles.toolbarSlider}
                      min="0"
                      max={Math.PI * 2}
                      step="0.1"
                      value={lightingSettings.environmentRotation}
                      onChange={(e) => updateSetting('environmentRotation', parseFloat(e.target.value))}
                    />
                  </div>

                  <div className={styles.toolbarSection}>
                    <label className={styles.toolbarLabel}>
                      Exposición: {lightingSettings.exposure.toFixed(1)}
                    </label>
                    <input
                      type="range"
                      className={styles.toolbarSlider}
                      min="0.2"
                      max="3"
                      step="0.1"
                      value={lightingSettings.exposure}
                      onChange={(e) => updateSetting('exposure', parseFloat(e.target.value))}
                    />
                  </div>
                </div>

                {/* Lights Section */}
                <div className={styles.toolbarGroup}>
                  <div className={styles.toolbarGroupTitle}>Luces</div>

                  <div className={styles.toolbarSection}>
                    <label className={styles.toolbarLabel}>
                      Principal: {lightingSettings.keyLightIntensity.toFixed(1)}
                    </label>
                    <input
                      type="range"
                      className={styles.toolbarSlider}
                      min="0"
                      max="4"
                      step="0.1"
                      value={lightingSettings.keyLightIntensity}
                      onChange={(e) => updateSetting('keyLightIntensity', parseFloat(e.target.value))}
                    />
                  </div>

                  <div className={styles.toolbarSection}>
                    <label className={styles.toolbarLabel}>Color principal</label>
                    <input
                      type="color"
                      className={styles.toolbarColor}
                      value={lightingSettings.keyLightColor}
                      onChange={(e) => updateSetting('keyLightColor', e.target.value)}
                    />
                  </div>

                  <div className={styles.toolbarSection}>
                    <label className={styles.toolbarLabel}>
                      Relleno: {lightingSettings.fillLightIntensity.toFixed(1)}
                    </label>
                    <input
                      type="range"
                      className={styles.toolbarSlider}
                      min="0"
                      max="2"
                      step="0.1"
                      value={lightingSettings.fillLightIntensity}
                      onChange={(e) => updateSetting('fillLightIntensity', parseFloat(e.target.value))}
                    />
                  </div>

                  <div className={styles.toolbarSection}>
                    <label className={styles.toolbarLabel}>
                      Trasera: {lightingSettings.rimLightIntensity.toFixed(1)}
                    </label>
                    <input
                      type="range"
                      className={styles.toolbarSlider}
                      min="0"
                      max="2"
                      step="0.1"
                      value={lightingSettings.rimLightIntensity}
                      onChange={(e) => updateSetting('rimLightIntensity', parseFloat(e.target.value))}
                    />
                  </div>
                </div>

                {/* Shadows Section */}
                <div className={styles.toolbarGroup}>
                  <div className={styles.toolbarGroupTitle}>Sombras</div>

                  <div className={styles.toolbarSection}>
                    <label className={styles.toolbarCheckbox}>
                      <input
                        type="checkbox"
                        checked={lightingSettings.showShadows}
                        onChange={(e) => updateSetting('showShadows', e.target.checked)}
                      />
                      <span>Mostrar sombras</span>
                    </label>
                  </div>

                  {lightingSettings.showShadows && (
                    <>
                      <div className={styles.toolbarSection}>
                        <label className={styles.toolbarLabel}>
                          Intensidad: {lightingSettings.shadowIntensity.toFixed(1)}
                        </label>
                        <input
                          type="range"
                          className={styles.toolbarSlider}
                          min="0"
                          max="1"
                          step="0.05"
                          value={lightingSettings.shadowIntensity}
                          onChange={(e) => updateSetting('shadowIntensity', parseFloat(e.target.value))}
                        />
                      </div>

                      <div className={styles.toolbarSection}>
                        <label className={styles.toolbarLabel}>
                          Difuminado: {lightingSettings.shadowBlur.toFixed(1)}
                        </label>
                        <input
                          type="range"
                          className={styles.toolbarSlider}
                          min="0.5"
                          max="5"
                          step="0.5"
                          value={lightingSettings.shadowBlur}
                          onChange={(e) => updateSetting('shadowBlur', parseFloat(e.target.value))}
                        />
                      </div>
                    </>
                  )}
                </div>

                <button
                  className={styles.toolbarReset}
                  onClick={() => setLightingSettings(defaultLightingSettings)}
                >
                  Restablecer todo
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
